/**
 * Workforce Candidates Pipeline Page
 *
 * Manages the recruitment pipeline with:
 * - Kanban-style pipeline view
 * - List view with filtering
 * - Candidate details and stage transitions
 * - Skills gap scoring display
 * - Bulk actions for pipeline management
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserPlus,
  Search,
  Filter,
  MoreVertical,
  ChevronRight,
  Mail,
  Phone,
  MapPin,
  Star,
  ArrowRight,
  Plus,
  Download,
  Upload,
  RefreshCw,
  LayoutGrid,
  List,
  X,
  UserCheck,
  AlertCircle,
  Edit2,
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { PageHeader, PageContainer } from '@/components/weavein';
import PhoneInput from '@/components/ui/PhoneInput';
import { useAppTranslation } from '@/contexts/TranslationContext';

// Helper to get auth token from admin dashboard localStorage
const getAuthToken = (): string | null => {
  return localStorage.getItem('auth_token');
};

interface Candidate {
  // Unified (member-based) identifier. This is a members.id, NOT a legacy
  // workforce_candidates.id.
  member_id: number;
  profile_id?: number;
  first_name: string;
  last_name: string;
  full_name?: string;
  email?: string;
  phone?: string;
  city?: string;
  state?: string;
  // Unified endpoint returns recruitment_status; legacy `status` kept optional
  // for backward-compatible pipeline-stage comparisons.
  recruitment_status?: string;
  status?: string;
  pipeline_stage: string;
  target_role_id?: number;
  target_role_name?: string;
  overall_match_score?: number;
  source?: string;
  created_at?: string;
  converted_to_user_id?: number;
}

interface PipelineStage {
  id: string;
  label: string;
  color: string;
  count: number;
}

const PIPELINE_STAGES: PipelineStage[] = [
  { id: 'awareness', label: 'Awareness', color: 'bg-paper-2 border-v2border', count: 0 },
  { id: 'interest', label: 'Interest', color: 'bg-blue-100 border-blue-300', count: 0 },
  { id: 'consideration', label: 'Consideration', color: 'bg-purple-100 border-purple-300', count: 0 },
  { id: 'intent', label: 'Intent', color: 'bg-amber-100 border-amber-300', count: 0 },
  { id: 'evaluation', label: 'Evaluation', color: 'bg-emerald-100 border-emerald-300', count: 0 },
  { id: 'onboarded', label: 'Onboarded', color: 'bg-green-100 border-green-300', count: 0 },
];

interface NewCandidateForm {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  source_type: string;
}

const initialFormState: NewCandidateForm = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  city: '',
  state: '',
  source_type: 'application',
};

// Backend SourceType enum values
const SOURCE_TYPES = [
  { value: 'application', label: 'Direct Application' },
  { value: 'referral', label: 'Referral' },
  { value: 'outreach', label: 'Outreach' },
  { value: 'skills_gap_agent', label: 'Skills Gap Agent' },
  { value: 'mentoring_program', label: 'Mentoring Program' },
];

const EMPLOYMENT_TYPES = [
  { value: 'full_time', label: 'Full-Time' },
  { value: 'part_time', label: 'Part-Time' },
  { value: 'contract', label: 'Contract' },
  { value: 'volunteer', label: 'Volunteer' },
];

// Edit Candidate Form interface
interface EditCandidateForm {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  city: string;
  state: string;
}

// Edit Candidate Modal Component
function EditCandidateModal({
  isOpen,
  onClose,
  candidate,
  onSave,
  saving,
  error,
}: {
  isOpen: boolean;
  onClose: () => void;
  candidate: Candidate;
  onSave: (data: Partial<EditCandidateForm>) => void;
  saving: boolean;
  error: string | null;
}) {
  const { t } = useAppTranslation();
  const [formData, setFormData] = useState<EditCandidateForm>({
    first_name: candidate.first_name,
    last_name: candidate.last_name,
    email: candidate.email || '',
    phone: candidate.phone || '',
    city: candidate.city || '',
    state: candidate.state || '',
  });

  // Update form data when candidate changes
  useEffect(() => {
    setFormData({
      first_name: candidate.first_name,
      last_name: candidate.last_name,
      email: candidate.email || '',
      phone: candidate.phone || '',
      city: candidate.city || '',
      state: candidate.state || '',
    });
  }, [candidate]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email || undefined,
      phone: formData.phone || undefined,
      city: formData.city || undefined,
      state: formData.state || undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-surface rounded-xl shadow-xl w-full max-w-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-ink">{t('workforce.candidates.editModal.title', 'Edit Candidate')}</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-paper-2 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.candidates.editModal.firstName', 'First Name')} *</label>
                <input
                  type="text"
                  required
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.candidates.editModal.lastName', 'Last Name')} *</label>
                <input
                  type="text"
                  required
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.candidates.editModal.email', 'Email')}</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.candidates.editModal.phone', 'Phone')}</label>
              <PhoneInput
                value={formData.phone}
                onChange={(v) => setFormData({ ...formData, phone: v })}
                className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.candidates.editModal.city', 'City')}</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.candidates.editModal.state', 'State (2-letter)')}</label>
                <input
                  type="text"
                  maxLength={2}
                  placeholder="CA"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-v2border rounded-lg hover:bg-paper font-medium"
              >
                {t('workforce.candidates.editModal.cancel', 'Cancel')}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-50 flex items-center justify-center"
              >
                {saving ? t('workforce.candidates.editModal.saving', 'Saving...') : (
                  <>
                    <Edit2 className="w-4 h-4 mr-2" />
                    {t('workforce.candidates.editModal.saveChanges', 'Save Changes')}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// Convert to User Modal Component
function ConvertToUserModal({
  isOpen,
  onClose,
  candidate,
  roles,
  onConvert,
  converting,
  error,
}: {
  isOpen: boolean;
  onClose: () => void;
  candidate: Candidate;
  roles: {id: number, title: string, role_code: string}[];
  onConvert: (roleId?: number, employmentType?: string) => void;
  converting: boolean;
  error: string | null;
}) {
  const { t } = useAppTranslation();
  const [selectedRoleId, setSelectedRoleId] = useState<string>(
    candidate.target_role_id?.toString() || ''
  );
  const [employmentType, setEmploymentType] = useState('full_time');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-surface rounded-xl shadow-xl w-full max-w-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-ink">{t('workforce.candidates.convertModal.title', 'Convert Candidate to User')}</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-paper-2 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="mb-6">
            <div className="flex items-center space-x-4 p-4 bg-paper rounded-lg">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <span className="text-emerald-600 font-bold">
                  {candidate.first_name[0]}{candidate.last_name[0]}
                </span>
              </div>
              <div>
                <p className="font-medium text-ink">
                  {candidate.first_name} {candidate.last_name}
                </p>
                <p className="text-sm text-ink-500">{candidate.email}</p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">{t('workforce.candidates.convertModal.thisWill', 'This will')}:</p>
                <ul className="mt-1 space-y-1 list-disc list-inside">
                  <li>{t('workforce.candidates.convertModal.createAccount', 'Create a user account with this candidate\'s information')}</li>
                  <li>{t('workforce.candidates.convertModal.addToRoster', 'Add them to the workforce roster')}</li>
                  <li>{t('workforce.candidates.convertModal.sendWelcome', 'Send a welcome email with login credentials')}</li>
                </ul>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.candidates.convertModal.roleAssignment', 'Role Assignment')}</label>
              <select
                value={selectedRoleId}
                onChange={(e) => setSelectedRoleId(e.target.value)}
                className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('workforce.candidates.convertModal.selectRole', 'Select a role...')}</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.title} ({role.role_code})
                  </option>
                ))}
              </select>
              {candidate.target_role_name && !selectedRoleId && (
                <p className="text-sm text-ink-500 mt-1">
                  {t('workforce.candidates.convertModal.willUseTargetRole', 'Will use target role')}: {candidate.target_role_name}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.candidates.convertModal.employmentType', 'Employment Type')}</label>
              <select
                value={employmentType}
                onChange={(e) => setEmploymentType(e.target.value)}
                className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {EMPLOYMENT_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex space-x-3 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-v2border rounded-lg hover:bg-paper font-medium"
            >
              {t('workforce.candidates.convertModal.cancel', 'Cancel')}
            </button>
            <button
              onClick={() => onConvert(selectedRoleId ? parseInt(selectedRoleId) : undefined, employmentType)}
              disabled={converting}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 flex items-center justify-center"
            >
              {converting ? (
                t('workforce.candidates.convertModal.converting', 'Converting...')
              ) : (
                <>
                  <UserCheck className="w-4 h-4 mr-2" />
                  {t('workforce.candidates.convertModal.convertToUser', 'Convert to User')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CandidatesPage() {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'kanban' | 'list'>('kanban');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterRole, setFilterRole] = useState<string>('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [newCandidate, setNewCandidate] = useState<NewCandidateForm>(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState<string | null>(null);
  const [roles, setRoles] = useState<{id: number, title: string, role_code: string}[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  useEffect(() => {
    fetchCandidates();
  }, [filterStatus, filterRole]);

  const fetchCandidates = async () => {
    const token = getAuthToken();
    if (!token) {
      console.warn('No auth token found for candidates page');
      setLoading(false);
      return;
    }

    // Get active CBO ID from localStorage (set by organization selector in App.tsx)
    const activeCboId = localStorage.getItem('active_cbo_id');

    try {
      let url = getWeaveinApiUrl('/api/workforce/unified-candidates/?limit=100');
      if (filterStatus) url += `&pipeline_stage=${filterStatus}`;
      if (filterRole) url += `&target_role_id=${filterRole}`;

      const headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
      };

      // Include X-CBO-ID header if active organization is set
      if (activeCboId) {
        headers['X-CBO-ID'] = activeCboId;
      }

      console.log('Fetching candidates with headers:', headers);

      const response = await fetch(url, { headers });

      if (response.ok) {
        const data = await response.json();
        console.log(`Fetched ${data.candidates?.length || 0} candidates for CBO ${activeCboId}`);
        setCandidates(data.candidates || []);
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
    } finally {
      setLoading(false);
    }
  };

  // Advance candidate one step along the pipeline.
  // NOTE: the unified endpoint only supports advancing to the NEXT stage, not
  // setting an arbitrary one, so the `stage` argument is advisory only.
  const setStage = async (memberId: number, _stage?: string) => {
    try {
      const token = getAuthToken();
      const activeCboId = localStorage.getItem('active_cbo_id');

      const headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      if (activeCboId) {
        headers['X-CBO-ID'] = activeCboId;
      }

      const response = await fetch(
        getWeaveinApiUrl(`/api/workforce/unified-candidates/${memberId}/advance-pipeline`),
        {
          method: 'POST',
          headers,
        }
      );

      const data = await response.json();
      console.log('Advance pipeline response:', response.status, data);

      if (response.ok) {
        // advance-pipeline returns { member_id, previous_stage, pipeline_stage }
        // (not a full candidate) — merge the new stage into the selected one.
        if (
          selectedCandidate &&
          selectedCandidate.member_id === memberId &&
          data.pipeline_stage
        ) {
          setSelectedCandidate({ ...selectedCandidate, pipeline_stage: data.pipeline_stage });
        }
        fetchCandidates();
      } else {
        console.error('Failed to advance candidate stage:', data);
        alert(`Failed to change stage: ${data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error advancing candidate stage:', error);
      alert('Error changing stage. Please try again.');
    }
  };

  // Advance candidate to next pipeline stage
  const advanceCandidate = async (candidateId: number) => {
    if (!selectedCandidate) return;

    const currentIndex = PIPELINE_STAGES.findIndex(s => s.id === selectedCandidate.pipeline_stage);
    if (currentIndex >= PIPELINE_STAGES.length - 1) {
      alert('Candidate is already at the final stage');
      return;
    }

    const nextStage = PIPELINE_STAGES[currentIndex + 1].id;
    await setStage(candidateId, nextStage);
  };

  const getCandidatesByStage = (stage: string) => {
    return candidates.filter(c => c.pipeline_stage === stage || c.status === stage);
  };

  const filteredCandidates = candidates.filter(c => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      c.first_name.toLowerCase().includes(query) ||
      c.last_name.toLowerCase().includes(query) ||
      c.email?.toLowerCase().includes(query) ||
      c.target_role_name?.toLowerCase().includes(query)
    );
  });

  const getScoreColor = (score?: number) => {
    if (!score) return 'text-ink-400';
    if (score >= 80) return 'text-emerald-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const fetchRoles = async () => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const activeCboId = localStorage.getItem('active_cbo_id');
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
      };
      if (activeCboId) {
        headers['X-CBO-ID'] = activeCboId;
      }

      const response = await fetch(getWeaveinApiUrl('/api/workforce/roles'), {
        headers,
      });

      if (response.ok) {
        const data = await response.json();
        setRoles(data.items || []);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const handleOpenConvertModal = () => {
    fetchRoles();
    setShowConvertModal(true);
    setConvertError(null);
  };

  const handleConvertToUser = async (roleId?: number, employmentType: string = 'full_time') => {
    if (!selectedCandidate) return;

    setConverting(true);
    setConvertError(null);

    try {
      const token = getAuthToken();
      const activeCboId = localStorage.getItem('active_cbo_id');
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
      if (activeCboId) {
        headers['X-CBO-ID'] = activeCboId;
      }

      const response = await fetch(
        getWeaveinApiUrl(`/api/workforce/unified-candidates/${selectedCandidate.member_id}/convert-to-user`),
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            // ConvertToUserRequest: role_id (required), start_date?, supervisor_id?, user_email?
            role_id: roleId || selectedCandidate.target_role_id,
            user_email: selectedCandidate.email || undefined,
          }),
        }
      );

      const result = await response.json();

      // Unified convert-to-user returns { user_id, member_id, roster_id, link_type }
      // (no `success` flag) — treat any 2xx as success.
      if (response.ok) {
        setShowConvertModal(false);
        setSelectedCandidate(null);
        fetchCandidates();
      } else {
        // Handle Pydantic validation errors (422) which return detail as an array
        let errorMessage = 'Failed to convert candidate';
        const detail = result.message || result.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join('; ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (detail) {
          errorMessage = JSON.stringify(detail);
        }
        setConvertError(errorMessage);
      }
    } catch (error) {
      console.error('Error converting candidate:', error);
      setConvertError('Network error. Please try again.');
    } finally {
      setConverting(false);
    }
  };

  const handleAddCandidate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);

    try {
      const token = getAuthToken();
      const activeCboId = localStorage.getItem('active_cbo_id');
      // Clean up payload - convert empty strings to null for optional fields
      const payload = {
        first_name: newCandidate.first_name,
        last_name: newCandidate.last_name,
        email: newCandidate.email || null,
        phone: newCandidate.phone || null,
        city: newCandidate.city || null,
        state: newCandidate.state || null,
        source_type: newCandidate.source_type,
      };

      const headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
      if (activeCboId) {
        headers['X-CBO-ID'] = activeCboId;
      }

      const response = await fetch(getWeaveinApiUrl('/api/workforce/unified-candidates/'), {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setShowAddModal(false);
        setNewCandidate(initialFormState);
        fetchCandidates();
      } else {
        const errorData = await response.json();
        // Handle Pydantic validation errors (422) which return detail as an array
        let errorMessage = 'Failed to add candidate';
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join('; ');
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (errorData.detail) {
          errorMessage = JSON.stringify(errorData.detail);
        }
        setSubmitError(errorMessage);
      }
    } catch (error) {
      console.error('Error adding candidate:', error);
      setSubmitError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateCandidate = async (updateData: Partial<EditCandidateForm>) => {
    if (!selectedCandidate) return;

    setEditSaving(true);
    setEditError(null);

    try {
      const token = getAuthToken();
      const activeCboId = localStorage.getItem('active_cbo_id');

      const headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
      if (activeCboId) {
        headers['X-CBO-ID'] = activeCboId;
      }

      const response = await fetch(
        getWeaveinApiUrl(`/api/workforce/unified-candidates/${selectedCandidate.member_id}`),
        {
          method: 'PUT',
          headers,
          body: JSON.stringify(updateData),
        }
      );

      if (response.ok) {
        // Unified PUT returns { success, member_id } (not a full candidate) —
        // merge the edited fields into the currently-selected candidate.
        await response.json();
        setShowEditModal(false);
        setSelectedCandidate({ ...selectedCandidate, ...updateData });
        fetchCandidates();
      } else {
        const errorData = await response.json();
        let errorMessage = 'Failed to update candidate';
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join('; ');
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (errorData.detail) {
          errorMessage = JSON.stringify(errorData.detail);
        }
        setEditError(errorMessage);
      }
    } catch (error) {
      console.error('Error updating candidate:', error);
      setEditError('Network error. Please try again.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleOpenEditModal = () => {
    setShowEditModal(true);
    setEditError(null);
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Workforce · Recruiting"
        title={t('workforce.candidates.title', 'Candidate Pipeline')}
        description={t('workforce.candidates.description', 'Manage recruitment and candidate tracking')}
        actions={
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-v2accent px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
          >
            <Plus className="w-4 h-4" />
            {t('workforce.candidates.addCandidate', 'Add Candidate')}
          </button>
        }
      />
      <PageContainer>
      <div className="space-y-6">
        {/* Toolbar */}
        <div className="bg-surface rounded-xl border border-v2border p-4">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('workforce.candidates.searchPlaceholder', 'Search candidates...')}
                className="w-full pl-10 pr-4 py-2 border border-v2border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            {/* Filters */}
            <div className="flex items-center space-x-3">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-v2border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">{t('workforce.candidates.allStages', 'All Stages')}</option>
                {PIPELINE_STAGES.map(stage => (
                  <option key={stage.id} value={stage.id}>{stage.label}</option>
                ))}
              </select>

              {/* View Toggle */}
              <div className="flex items-center bg-paper-2 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('kanban')}
                  className={`p-2 rounded ${viewMode === 'kanban' ? 'bg-surface shadow-sm' : ''}`}
                  title={t('workforce.candidates.kanbanView', 'Kanban View')}
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-surface shadow-sm' : ''}`}
                  title={t('workforce.candidates.listView', 'List View')}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>

              <button
                onClick={fetchCandidates}
                className="p-2 border border-v2border rounded-lg hover:bg-paper"
                title={t('workforce.candidates.refresh', 'Refresh')}
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Kanban View */}
        {viewMode === 'kanban' && (
          <div className="flex space-x-4 overflow-x-auto pb-4">
            {PIPELINE_STAGES.map((stage) => {
              const stageCandidates = getCandidatesByStage(stage.id);
              return (
                <div
                  key={stage.id}
                  className={`flex-shrink-0 w-80 rounded-xl border-2 ${stage.color}`}
                >
                  {/* Stage Header */}
                  <div className="px-4 py-3 border-b border-v2border bg-white/50 rounded-t-xl">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-ink">{stage.label}</h3>
                      <span className="text-sm text-ink-500 bg-surface px-2 py-0.5 rounded-full">
                        {stageCandidates.length}
                      </span>
                    </div>
                  </div>

                  {/* Candidates */}
                  <div className="p-2 space-y-2 min-h-[200px] max-h-[600px] overflow-y-auto">
                    {loading ? (
                      <div className="p-4 text-center text-ink-500">{t('workforce.candidates.loading', 'Loading...')}</div>
                    ) : stageCandidates.length === 0 ? (
                      <div className="p-4 text-center text-ink-400 text-sm">
                        {t('workforce.candidates.noInStage', 'No candidates in this stage')}
                      </div>
                    ) : (
                      stageCandidates.map((candidate) => (
                        <div
                          key={candidate.member_id}
                          onClick={() => setSelectedCandidate(candidate)}
                          className="bg-surface rounded-lg border border-v2border p-3 cursor-pointer hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-ink">
                                {candidate.first_name} {candidate.last_name}
                              </p>
                              <p className="text-sm text-ink-500 truncate">{candidate.email}</p>
                            </div>
                            {candidate.overall_match_score && (
                              <div className={`flex items-center ${getScoreColor(candidate.overall_match_score)}`}>
                                <Star className="w-4 h-4 mr-1" />
                                <span className="text-sm font-medium">{candidate.overall_match_score}%</span>
                              </div>
                            )}
                          </div>
                          {candidate.target_role_name && (
                            <p className="mt-2 text-xs text-ink-400">
                              {t('workforce.candidates.target', 'Target')}: {candidate.target_role_name}
                            </p>
                          )}
                          {candidate.city && (
                            <div className="mt-2 flex items-center text-xs text-ink-400">
                              <MapPin className="w-3 h-3 mr-1" />
                              {candidate.city}, {candidate.state}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <div className="bg-surface rounded-xl border border-v2border overflow-hidden">
            <table className="w-full">
              <thead className="bg-paper border-b border-v2border">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase">{t('workforce.candidates.table.candidate', 'Candidate')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase">{t('workforce.candidates.table.contact', 'Contact')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase">{t('workforce.candidates.table.targetRole', 'Target Role')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase">{t('workforce.candidates.table.stage', 'Stage')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase">{t('workforce.candidates.table.matchScore', 'Match Score')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase">{t('workforce.candidates.table.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-ink-500">{t('workforce.candidates.loading', 'Loading...')}</td>
                  </tr>
                ) : filteredCandidates.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-ink-500">{t('workforce.candidates.noCandidates', 'No candidates found')}</td>
                  </tr>
                ) : (
                  filteredCandidates.map((candidate) => (
                    <tr key={candidate.member_id} className="hover:bg-paper">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full bg-paper-2 flex items-center justify-center mr-3">
                            <span className="text-ink-500 font-medium">
                              {candidate.first_name[0]}{candidate.last_name[0]}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-ink">
                              {candidate.first_name} {candidate.last_name}
                            </p>
                            {candidate.city && (
                              <p className="text-sm text-ink-500">{candidate.city}, {candidate.state}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div className="flex items-center text-ink-500">
                            <Mail className="w-4 h-4 mr-1" />
                            {candidate.email}
                          </div>
                          {candidate.phone && (
                            <div className="flex items-center text-ink-500 mt-1">
                              <Phone className="w-4 h-4 mr-1" />
                              {candidate.phone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-ink-500">
                          {candidate.target_role_name || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          PIPELINE_STAGES.find(s => s.id === candidate.pipeline_stage)?.color || 'bg-paper-2'
                        }`}>
                          {candidate.pipeline_stage}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {candidate.overall_match_score ? (
                          <div className={`flex items-center ${getScoreColor(candidate.overall_match_score)}`}>
                            <Star className="w-4 h-4 mr-1" />
                            <span className="font-medium">{candidate.overall_match_score}%</span>
                          </div>
                        ) : (
                          <span className="text-ink-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => setSelectedCandidate(candidate)}
                          className="text-emerald-600 hover:text-emerald-700 font-medium text-sm"
                        >
                          {t('workforce.candidates.viewDetails', 'View Details')}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Candidate Detail Slide-out */}
        {selectedCandidate && (
          <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black/50" onClick={() => setSelectedCandidate(null)} />
            <div className="absolute right-0 top-0 bottom-0 w-full max-w-lg bg-surface shadow-xl">
              <div className="h-full flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-v2border flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-ink">{t('workforce.candidates.detail.title', 'Candidate Details')}</h2>
                  <button
                    onClick={() => setSelectedCandidate(null)}
                    className="p-2 hover:bg-paper-2 rounded"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                  <div className="space-y-6">
                    {/* Profile */}
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                        <span className="text-emerald-600 font-bold text-xl">
                          {selectedCandidate.first_name[0]}{selectedCandidate.last_name[0]}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-ink">
                          {selectedCandidate.first_name} {selectedCandidate.last_name}
                        </h3>
                        <p className="text-ink-500">{selectedCandidate.target_role_name || t('workforce.candidates.detail.noTargetRole', 'No target role')}</p>
                      </div>
                    </div>

                    {/* Match Score */}
                    {selectedCandidate.overall_match_score && (
                      <div className="bg-paper rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <span className="text-ink-500">{t('workforce.candidates.detail.matchScore', 'Match Score')}</span>
                          <span className={`font-display text-2xl font-semibold ${getScoreColor(selectedCandidate.overall_match_score)}`}>
                            {selectedCandidate.overall_match_score}%
                          </span>
                        </div>
                        <div className="mt-2 h-2 bg-paper-2 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-emerald-500 rounded-full"
                            style={{ width: `${selectedCandidate.overall_match_score}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Contact Info */}
                    <div>
                      <h4 className="font-medium text-ink mb-3">{t('workforce.candidates.detail.contactInfo', 'Contact Information')}</h4>
                      <div className="space-y-2">
                        <div className="flex items-center text-ink-500">
                          <Mail className="w-4 h-4 mr-3" />
                          {selectedCandidate.email}
                        </div>
                        {selectedCandidate.phone && (
                          <div className="flex items-center text-ink-500">
                            <Phone className="w-4 h-4 mr-3" />
                            {selectedCandidate.phone}
                          </div>
                        )}
                        {selectedCandidate.city && (
                          <div className="flex items-center text-ink-500">
                            <MapPin className="w-4 h-4 mr-3" />
                            {selectedCandidate.city}, {selectedCandidate.state}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Pipeline Stage */}
                    <div>
                      <h4 className="font-medium text-ink mb-3">{t('workforce.candidates.detail.pipelineStage', 'Pipeline Stage')}</h4>
                      <div className="flex flex-wrap gap-2">
                        {PIPELINE_STAGES.map((stage) => (
                          <button
                            key={stage.id}
                            onClick={() => setStage(selectedCandidate.member_id, stage.id)}
                            disabled={selectedCandidate.pipeline_stage === stage.id}
                            className={`px-3 py-1.5 rounded-full text-sm font-medium border-2 transition-colors ${
                              selectedCandidate.pipeline_stage === stage.id
                                ? stage.color + ' border-current cursor-default'
                                : 'border-v2border hover:border-emerald-400 hover:bg-emerald-50 cursor-pointer'
                            }`}
                          >
                            {stage.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="px-6 py-4 border-t border-v2border space-y-3">
                  {/* Convert to User button - only show for onboarded candidates */}
                  {(selectedCandidate.pipeline_stage === 'onboarded' || selectedCandidate.status === 'onboarded') && !selectedCandidate.converted_to_user_id && (
                    <button
                      onClick={handleOpenConvertModal}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center"
                    >
                      <UserCheck className="w-4 h-4 mr-2" />
                      {t('workforce.candidates.detail.convertToUser', 'Convert to User')}
                    </button>
                  )}

                  {/* Already converted indicator */}
                  {selectedCandidate.converted_to_user_id && (
                    <div className="w-full px-4 py-2 bg-green-50 text-green-700 rounded-lg font-medium flex items-center justify-center">
                      <UserCheck className="w-4 h-4 mr-2" />
                      {t('workforce.candidates.detail.convertedToUser', 'Converted to User')} (ID: {selectedCandidate.converted_to_user_id})
                    </div>
                  )}

                  <div className="flex space-x-3">
                    <button
                      onClick={handleOpenEditModal}
                      className="flex-1 px-4 py-2 border border-v2border rounded-lg hover:bg-paper font-medium flex items-center justify-center"
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      {t('workforce.candidates.detail.edit', 'Edit')}
                    </button>
                    <button
                      onClick={() => advanceCandidate(selectedCandidate.member_id)}
                      disabled={selectedCandidate.pipeline_stage === 'onboarded'}
                      className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('workforce.candidates.detail.advance', 'Advance')} <ArrowRight className="w-4 h-4 ml-2" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Candidate Modal */}
        {showAddModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="fixed inset-0 bg-black/50" onClick={() => setShowAddModal(false)} />
              <div className="relative bg-surface rounded-xl shadow-xl w-full max-w-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-ink">{t('workforce.candidates.addModal.title', 'Add New Candidate')}</h2>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="p-2 hover:bg-paper-2 rounded"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {submitError && (
                  <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                    {submitError}
                  </div>
                )}

                <form onSubmit={handleAddCandidate} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.candidates.addModal.firstName', 'First Name')} *</label>
                      <input
                        type="text"
                        required
                        value={newCandidate.first_name}
                        onChange={(e) => setNewCandidate({ ...newCandidate, first_name: e.target.value })}
                        className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.candidates.addModal.lastName', 'Last Name')} *</label>
                      <input
                        type="text"
                        required
                        value={newCandidate.last_name}
                        onChange={(e) => setNewCandidate({ ...newCandidate, last_name: e.target.value })}
                        className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.candidates.addModal.email', 'Email')} *</label>
                    <input
                      type="email"
                      required
                      value={newCandidate.email}
                      onChange={(e) => setNewCandidate({ ...newCandidate, email: e.target.value })}
                      className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.candidates.addModal.phone', 'Phone')}</label>
                    <PhoneInput
                      value={newCandidate.phone}
                      onChange={(v) => setNewCandidate({ ...newCandidate, phone: v })}
                      className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.candidates.addModal.city', 'City')}</label>
                      <input
                        type="text"
                        value={newCandidate.city}
                        onChange={(e) => setNewCandidate({ ...newCandidate, city: e.target.value })}
                        className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.candidates.addModal.state', 'State (2-letter)')}</label>
                      <input
                        type="text"
                        maxLength={2}
                        placeholder="CA"
                        value={newCandidate.state}
                        onChange={(e) => setNewCandidate({ ...newCandidate, state: e.target.value.toUpperCase() })}
                        className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">{t('workforce.candidates.addModal.source', 'Source')}</label>
                    <select
                      value={newCandidate.source_type}
                      onChange={(e) => setNewCandidate({ ...newCandidate, source_type: e.target.value })}
                      className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      {SOURCE_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      className="flex-1 px-4 py-2 border border-v2border rounded-lg hover:bg-paper font-medium"
                    >
                      {t('workforce.candidates.addModal.cancel', 'Cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-50"
                    >
                      {submitting ? t('workforce.candidates.addModal.adding', 'Adding...') : t('workforce.candidates.addModal.addCandidate', 'Add Candidate')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Convert to User Modal */}
        {showConvertModal && selectedCandidate && (
          <ConvertToUserModal
            isOpen={showConvertModal}
            onClose={() => setShowConvertModal(false)}
            candidate={selectedCandidate}
            roles={roles}
            onConvert={handleConvertToUser}
            converting={converting}
            error={convertError}
          />
        )}

        {/* Edit Candidate Modal */}
        {showEditModal && selectedCandidate && (
          <EditCandidateModal
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            candidate={selectedCandidate}
            onSave={handleUpdateCandidate}
            saving={editSaving}
            error={editError}
          />
        )}
      </div>
      </PageContainer>
    </div>
  );
}
